import { MultiSelectOption } from '@raisin/components-lib/*';
import { fetchProductDocument } from 'api/varify';
import messages from 'messages';
import { ReactNode } from 'react';
import { FinancialInstitutionType, MaturityType } from 'types';
import rawInstitutionOptions from './institutionOptions.json';

export type OfferSortingKey = 'bankName' | 'productName' | 'apy' | 'maturityDisplay' | 'earnings';

export const headerColumns: {
  title: string;
  sortable: boolean;
  sortingKey: OfferSortingKey;
}[] = [
  { title: messages.usOfferTableHeaderBank, sortable: true, sortingKey: 'bankName' },
  { title: messages.usOfferTableHeaderProduct, sortable: true, sortingKey: 'productName' },
  { title: messages.usOfferTableHeaderApy, sortable: true, sortingKey: 'apy' },
  { title: messages.usOfferTableHeaderMaturity, sortable: true, sortingKey: 'maturityDisplay' },
  { title: messages.usOfferTableHeaderAnnualizedEarnings, sortable: false, sortingKey: 'earnings' },
];

export type HeaderStringFields = 'Bank' | 'Product' | 'Maturity';
export type SortDirection = 'asc' | 'desc';

export interface TabProps {
  label: string;
  content: string | ReactNode;
  id: string;
  disabled?: boolean;
}

// NOSONAR
export const financialInstitutionOptions: MultiSelectOption[] = rawInstitutionOptions;

export const financialInstitutionMap = new Map(
  financialInstitutionOptions.map((option) => [option.value, option.text]),
);

export const getInstitutionChipLabels = (institutionCmsValues: FinancialInstitutionType[]) =>
  institutionCmsValues.reduce((acc: string[], current) => {
    const label = financialInstitutionMap.get(current);
    if (!label) return acc;
    acc.push(label);
    return acc;
  }, []);

export const categoryTabsProps = {
  FEATURED: {
    id: 'FEATURED',
    label: 'Featured Products',
  },
  SAVINGS_ACCOUNT: {
    id: 'SAVINGS',
    label: 'Savings',
  },
  HIGH_YIELD_CD: {
    id: 'HYCD',
    label: 'High-yield CDs',
  },
  NO_PENALTY_CD: {
    id: 'NPCD',
    label: 'No penalty CDs',
  },
};
export const calculateEarnings = (apy: number, depositAmount: number) => {
  // standardizes similar but not precisely equal APYs
  const apyMultiplier = Number(apy.toFixed(4));
  const earnings = depositAmount * apyMultiplier;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(earnings);
  return `${formatted}`;
};

export const openProductTerms = async (productId: string): Promise<string | void> => {
  const res = await fetchProductDocument(productId);
  const base64Content = res?.content;
  if (!base64Content) {
    return;
  }

  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  // encode base64 to byte format for blob
  const convertDataToBinary = (data: string) => {
    const raw = atob(data);
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));
    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  };

  const byteCharacters = convertDataToBinary(base64Content);

  const newBlob = new Blob([byteCharacters], { type: 'application/pdf' });
  const nav = window.navigator as any;

  // IE doesn't allow using a blob object directly as link href (it is necessary to use msSaveOrOpenBlob)
  if (nav && nav.msSaveOrOpenBlob) {
    nav.msSaveOrOpenBlob(newBlob);
  }
  const url = URL.createObjectURL(newBlob);
  url && window.open(url, '_blank');
  return;
};
const units = new Map<MaturityType, string>([
  ['DAYS', 'day'],
  ['MONTHS', 'month'],
  ['YEARS', 'year'],
]);

export function formatMaturity(duration?: number, unit?: MaturityType): string {
  if (!duration || !unit) return '';

  const unitString = units.get(unit)!;
  const pluralize = duration !== 1 ? 's' : '';

  return `${duration} ${unitString}${pluralize}`;
}

type FormatPercentOptions = Pick<
  Intl.NumberFormatOptions,
  'currency' | 'minimumFractionDigits' | 'maximumFractionDigits'
>;

export function formatPercents(value: number, options?: FormatPercentOptions): string {
  const { maximumFractionDigits = 2, minimumFractionDigits = maximumFractionDigits } =
    options || ({} as FormatPercentOptions);

  return value.toLocaleString('en-US', {
    style: 'percent',
    maximumFractionDigits,
    minimumFractionDigits,
  });
}

export const updateSelectedFilters = (
  currentSelected: MultiSelectOption[],
  nextSelected: MultiSelectOption[],
  allOptions: MultiSelectOption[],
) => {
  const hasSelectAll = nextSelected.some((opt) => opt.value === 'All');
  const hadSelectAll = currentSelected.some((opt) => opt.value === 'All');
  const allOptionsSelected = nextSelected.length === allOptions.length - 1;

  // previously "select all" selected.
  if (hadSelectAll) {
    const selectedWithoutSelectAll = nextSelected.filter((sel) => sel.value !== 'All');
    // user clicked "select all" ? selected array is empty : user clicked normal option and we remove "select all" as well.
    return !hasSelectAll ? [] : selectedWithoutSelectAll;
  }

  // Select all was target || All others have been selected manually. Set all options to selected
  if (hasSelectAll || allOptionsSelected) {
    return allOptions;
  }

  // Basic case. Add/remove filters w/o affecting "all"
  return nextSelected;
};
